import * as React from "react";
import { IBasePickerSuggestionsProps, Label, IPersonaProps, NormalPeoplePicker, PeoplePickerItemSuggestion, IPeoplePickerItemSuggestionStyles } from "@fluentui/react";
import { getSharePointSiteUsers, getSharePointGroupUsers } from "../../services/SharePointRestServices";
import { IPeoplePickerProps } from "../../interfaces/props/IPeoplePickerProps";
import authModule from "../../../../utilities/AuthModule";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";
import { validateEmail } from "../helpers/validateEmail";

const suggestionProps: IBasePickerSuggestionsProps = {
  suggestionsHeaderText: "Suggested People",
  mostRecentlyUsedHeaderText: "Suggested Contacts",
  noResultsFoundText: "No results found",
  loadingText: "Loading",
  showRemoveButtons: true,
  suggestionsAvailableAlertText: "People Picker Suggestions available",
  suggestionsContainerAriaLabel: "Suggested contacts"
};

const suggestionStyles: Partial<IPeoplePickerItemSuggestionStyles> = {
  personaWrapper: {
    width: "auto"
  }
}

export const PeoplePicker: React.FunctionComponent<IPeoplePickerProps> = ({
  webUrl,
  displayName,
  isRequired,
  error,
  itemLimit,
  UserSelectionScope,
  updateInputValidationStatus,
  inputValidator,
  inputValue
}) => {
  const [peoplePickerList, setPeoplePickerList] = React.useState<IPersonaProps[]>([]);
  const [initialPickerList, setInitialPickerList] = React.useState<IPersonaProps[]>([]);

  React.useEffect(() => {
    getUsers();
  }, []);

  React.useEffect(() => {
    if (inputValue?.value?.length > 0 && peoplePickerList?.length > 0) {
      const initialUsers: IPersonaProps[] = peoplePickerList.filter(x => inputValue.value.includes(x.id));
      if (initialUsers.length > 0) {
        setInitialPickerList(initialUsers);
      }
      else {
        setInitialPickerList([]);
      }
    }
    else {
      setInitialPickerList([]);
    }
  }, [inputValue?.value, peoplePickerList]);

  const getUsers = async () => {
    const accessToken = await authModule.getSharePointAccessToken();
    let noUserGroupAssigned = !UserSelectionScope || UserSelectionScope == "0";
    if (noUserGroupAssigned) {
      let users = await getSharePointSiteUsers(accessToken, webUrl);
      if (Array.isArray(users) && users.length > 0) {
        setPeoplePickerList(users
          .map(item => {
            let secondaryText: string;
            if (item.Email) {
              secondaryText = item.Email;
            }
            else if (validateEmail(item.UserPrincipalName)) {
              secondaryText = item.UserPrincipalName;
            }

            return secondaryText
              ? { text: item.Title, id: item.Id, secondaryText }
              : null;
          })
          .filter(usr => usr)
        );
      }
      return;
    }

    let usergroups = await getSharePointGroupUsers(accessToken, webUrl, UserSelectionScope);
    setPeoplePickerList(usergroups.map(item => ({ text: item.Title, id: item.Id })));
  };

  const onFilterChanged = (
    filterText: string,
    currentPersonas: IPersonaProps[]
  ): IPersonaProps[] | Promise<IPersonaProps[]> => {
    if (filterText) {
      let filteredPersonas: IPersonaProps[] = filterPersonasByText(filterText);
      return sortResults(filterText, removeDuplicates(filteredPersonas, currentPersonas));
    } else {
      return [];
    }
  };

  const filterPersonasByText = (filterText: string): IPersonaProps[] => {
    return peoplePickerList.filter(item => doesTextInclude(item, filterText));
  };

  const handleOnChange = (items?: IPersonaProps[]) => {
    let personIds = items.map(item => parseInt(item.id));
    updateInputValidationStatus(inputValidator.validate(personIds));
  };

  const onRenderSuggestionsItem = React.useCallback((personaProps, suggestionsProps) => (
    <PeoplePickerItemSuggestion
      personaProps={personaProps}
      suggestionsProps={suggestionsProps}
      styles={suggestionStyles} />
  ), []);

  return (
    <div>
      {displayName && <Label required={isRequired()}>{displayName}</Label>}
      <NormalPeoplePicker
        disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
        onRenderSuggestionsItem={onRenderSuggestionsItem}
        onResolveSuggestions={onFilterChanged}
        getTextFromItem={getTextFromItem}
        pickerSuggestionsProps={suggestionProps}
        className={"ms-PeoplePicker"}
        key={"preselected"}
        itemLimit={itemLimit}
        removeButtonAriaLabel={"Remove"}
        selectedItems={initialPickerList}
        onChange={handleOnChange}
        resolveDelay={300}
      />
      {/* {displayError()} */}
    </div>
  );
};

const sortResults = (input: string, data: IPersonaProps[]): IPersonaProps[] => {
  try {
    let first: IPersonaProps[] = [];
    let others: IPersonaProps[] = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i].text.indexOf(input) == 0 || data[i].secondaryText.indexOf(input) == 0) {
        first.push(data[i]);
      } else {
        others.push(data[i]);
      }
    }
    first.sort((a, b) => a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1);
    others.sort((a, b) => a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1);
    return (first.concat(others));
  } catch (error) {
    console.warn(error);
  }
  return data;
}

function doesTextInclude(item: IPersonaProps, filterText: string): boolean {
  // return text.toLowerCase().indexOf(filterText?.trim().toLowerCase()) === 0;
  const searchText: string = filterText?.trim().toLowerCase();
  return item.text.toLowerCase().indexOf(searchText) !== -1
    || item.secondaryText.toLowerCase().indexOf(searchText) !== -1;
}

function removeDuplicates(personas: IPersonaProps[], possibleDupes: IPersonaProps[]) {
  return personas.filter(persona => !listContainsPersona(persona, possibleDupes));
}

function listContainsPersona(persona: IPersonaProps, personas: IPersonaProps[]) {
  if (!personas || !personas.length || personas.length === 0) {
    return false;
  }
  return personas.filter(item => item.id === persona.id).length > 0;
}

function getTextFromItem(persona: IPersonaProps): string {
  return persona.text as string;
}

export default PeoplePicker;
